/*
 * CMP-009 Select — Phase A1 (Figma 17:9252, menus 80:6693/80:6918, a11y rules 40:9057).
 * ARIA select-only combobox: button[role=combobox] + ul[role=listbox].
 * Keys: Enter/Space/ArrowDown/ArrowUp open; arrows move, Home/End jump, type-ahead,
 * Enter selects, Escape closes and returns focus to the control, Tab closes.
 * No dependencies. Pure helpers are exported for node:test.
 */
(function (root) {
  /** Next enabled option index for a key (no wrap), or null when the key is not handled. */
  function getNextOptionIndex(key, current, disabled) {
    var last = disabled.length - 1;
    function scan(from, step) {
      for (var i = from; i >= 0 && i <= last; i += step) if (!disabled[i]) return i;
      return null;
    }
    if (key === 'ArrowDown') {
      var n = scan(current + 1, 1);
      return n === null ? current : n;
    }
    if (key === 'ArrowUp') {
      var p = scan(current - 1, -1);
      return p === null ? current : p;
    }
    if (key === 'Home') return scan(0, 1);
    if (key === 'End') return scan(last, -1);
    return null;
  }

  /** Index of the next enabled option whose label starts with the typed text (cycles from current). */
  function getTypeaheadIndex(text, labels, current, disabled) {
    var q = text.toLowerCase();
    for (var i = 1; i <= labels.length; i++) {
      var idx = (current + i) % labels.length;
      if (!disabled[idx] && labels[idx].toLowerCase().indexOf(q) === 0) return idx;
    }
    return -1;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { getNextOptionIndex: getNextOptionIndex, getTypeaheadIndex: getTypeaheadIndex };
  }

  if (typeof customElements === 'undefined' || customElements.get('nc-select')) return;

  class NcSelect extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[role="combobox"]');
      this.list = this.querySelector('[role="listbox"]');
      this.input = this.querySelector('input[type="hidden"]');
      this.valueEl = this.querySelector('[data-nc-select-value]');
      if (!this.button || !this.list) return;
      this.options = Array.prototype.slice.call(this.list.querySelectorAll('[role="option"]'));
      this.buffer = '';
      var sel = this.options.findIndex(function (o) { return o.getAttribute('aria-selected') === 'true'; });
      this.selected = sel;
      this.active = sel < 0 ? this.firstEnabled() : sel;
      this.button.addEventListener('click', this.onButtonClick.bind(this));
      this.button.addEventListener('keydown', this.onKeydown.bind(this));
      this.list.addEventListener('click', this.onOptionClick.bind(this));
      this.list.addEventListener('mousemove', this.onOptionHover.bind(this));
      this.list.addEventListener('mousedown', function (e) { e.preventDefault(); });
      document.addEventListener('click', this.onDocumentClick.bind(this));
      this.button.addEventListener('blur', this.onBlur.bind(this));
    }

    get isOpen() { return this.button.getAttribute('aria-expanded') === 'true'; }
    disabledFlags() { return this.options.map(function (o) { return o.getAttribute('aria-disabled') === 'true'; }); }
    firstEnabled() {
      var i = this.disabledFlags().indexOf(false);
      return i < 0 ? 0 : i;
    }

    open() {
      if (this.button.disabled || this.isOpen) return;
      this.button.setAttribute('aria-expanded', 'true');
      this.list.hidden = false;
      this.setActive(this.selected >= 0 ? this.selected : this.firstEnabled());
    }

    close(restoreFocus) {
      if (!this.isOpen) return;
      this.button.setAttribute('aria-expanded', 'false');
      this.button.removeAttribute('aria-activedescendant');
      this.list.hidden = true;
      if (restoreFocus) this.button.focus();
    }

    setActive(index) {
      this.active = index;
      this.options.forEach(function (o, i) { o.classList.toggle('nc-select__option--active', i === index); });
      var opt = this.options[index];
      if (opt) {
        this.button.setAttribute('aria-activedescendant', opt.id);
        if (opt.scrollIntoView) opt.scrollIntoView({ block: 'nearest' });
      }
    }

    choose(index) {
      var opt = this.options[index];
      if (!opt || opt.getAttribute('aria-disabled') === 'true') return;
      this.selected = index;
      this.options.forEach(function (o, i) { o.setAttribute('aria-selected', i === index ? 'true' : 'false'); });
      if (this.valueEl) this.valueEl.textContent = opt.textContent.trim();
      this.button.removeAttribute('data-placeholder');
      if (this.input) this.input.value = opt.getAttribute('data-value') || '';
      this.dispatchEvent(new CustomEvent('nc-select:change', {
        bubbles: true,
        detail: { value: opt.getAttribute('data-value'), index: index }
      }));
    }

    onButtonClick() { this.isOpen ? this.close(false) : this.open(); }
    onBlur() { this.close(false); }
    onDocumentClick(e) { if (!this.contains(e.target)) this.close(false); }

    onOptionHover(e) {
      var opt = e.target.closest('[role="option"]');
      if (!opt || opt.getAttribute('aria-disabled') === 'true') return;
      var i = this.options.indexOf(opt);
      if (i !== this.active) this.setActive(i);
    }

    onOptionClick(e) {
      var opt = e.target.closest('[role="option"]');
      if (!opt) return;
      var i = this.options.indexOf(opt);
      if (opt.getAttribute('aria-disabled') === 'true') return;
      this.choose(i);
      this.close(true);
    }

    onKeydown(e) {
      var key = e.key;
      if (!this.isOpen) {
        if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
          e.preventDefault();
          this.open();
        }
        return;
      }
      if (key === 'Escape') { e.preventDefault(); this.close(true); return; }
      if (key === 'Tab') { this.close(false); return; }
      if (key === 'Enter' || key === ' ') {
        e.preventDefault();
        this.choose(this.active);
        this.close(true);
        return;
      }
      var next = getNextOptionIndex(key, this.active, this.disabledFlags());
      if (next !== null) { e.preventDefault(); this.setActive(next); return; }
      if (key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        var self = this;
        this.buffer += key;
        clearTimeout(this.bufferTimer);
        this.bufferTimer = setTimeout(function () { self.buffer = ''; }, 600);
        var labels = this.options.map(function (o) { return o.textContent.trim(); });
        var hit = getTypeaheadIndex(this.buffer, labels, this.active - (this.buffer.length > 1 ? 1 : 0), this.disabledFlags());
        if (hit >= 0) this.setActive(hit);
      }
    }
  }

  customElements.define('nc-select', NcSelect);
})(typeof window !== 'undefined' ? window : globalThis);
